import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import Actions from '@app/action';
import Assets from '@app/assets';
import I18n from '@module/i18n';
import TextField from '@component/form/element/textfield';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import { verify } from '@app/transfer-bundle/store/actions';
import Spinner from '@app/component/spinner';
import { WHITE } from '@app/constant/colors';
import { VALIDATION_IDENTIFIERS } from '@app/constant/validation-identifiers';
import { AppConfig } from '@app/core-bundle/config/app-config';
import Icon from '@component/icon';
import { actions as TransferActionTypes } from '@app/action/transfer';

function EmailVerification({ prePopulatedEmail, isLoggedIn }) {
  const dispatch = useDispatch();
  const [verificationCode, setVerificationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validState, setValidState] = useState(null);

  const trimmedVerificationCode = verificationCode.trim();

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      dispatch(Actions.Notification.clearTransfer());
      setIsSubmitting(true);
      setValidState(null);
      if (isLoggedIn) {
        return await dispatch(
          Actions.User.verifyAccountEmail(trimmedVerificationCode)
        );
      }
      return await dispatch(verify({ token: trimmedVerificationCode }));
    } catch {
      dispatch(
        Actions.Notification.pushTransfer({
          text: I18n.t('transfer.email-verification.error'),
          align: '.email-verification__textfield',
        })
      );
      setValidState(VALIDATION_IDENTIFIERS.NOT_VALID);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleResendButtonClick() {
    dispatch(Actions.Notification.clearTransfer());
    dispatch(Actions.Transfer.setState(TRANSFER_STATES.VERIFY_RESEND));
  }

  const isSubmitButtonActive =
    trimmedVerificationCode.length ===
    AppConfig.transfer.verificationCodeLength;
  const submitButtonClassName = classnames(
    'transfer__button',
    'email-verification__submit-button',
    {
      'transfer__button--inactive': !isSubmitButtonActive,
    }
  );

  function handleBackButtonClick() {
    dispatch(Actions.Notification.clearTransfer());
    dispatch(Actions.Transfer.setState(TRANSFER_STATES.INIT));
    dispatch(Actions.Transfer.resetVerificationRequired());
    dispatch({ type: TransferActionTypes.TRANSFER_SET_ID, id: false });
  }

  return (
    <form
      className="transfer__window email-verification"
      onSubmit={handleSubmit}
    >
      {validState === VALIDATION_IDENTIFIERS.NOT_VALID && (
        <div className="email-verification__mobile-error">
          {I18n.t('transfer.email-verification.error')}
        </div>
      )}
      <div className="transfer__contents email-verification__contents">
        <img
          className="email-verification__lock"
          src={Assets.lookup('transfer_window/email-verification-lock.svg')}
          alt="Lock"
        />
        <h2 className="email-verification__title">
          {I18n.t('transfer.email-verification.title')}
        </h2>
        <p
          className="email-verification__body"
          dangerouslySetInnerHTML={{
            __html: I18n.t('transfer.email-verification.body', {
              account_email: prePopulatedEmail,
            }),
          }}
        />
        <TextField
          name="verification-code"
          placeholder={I18n.t('transfer.email-verification.verification-code')}
          className="email-verification__textfield"
          inputClassName="email-verification__textfield-input"
          onChange={(event) => setVerificationCode(event.target.value)}
          value={verificationCode}
          valid={validState}
          maxLength={AppConfig.transfer.verificationCodeLength}
        />
        <button
          className="email-verification__resend-button"
          onClick={handleResendButtonClick}
          type="button"
        >
          {I18n.t('transfer.email-verification.resend')}
        </button>
      </div>
      <div className="transfer__footer email-verification__footer">
        {!isLoggedIn && (
          <Icon
            type="TransferBack"
            className="transfer__back"
            onClick={handleBackButtonClick}
            label={I18n.t('transfer.email-verification.back')}
            buttonType="button"
          />
        )}
        <button
          className={submitButtonClassName}
          onClick={handleSubmit}
          disabled={!isSubmitButtonActive}
          type="submit"
        >
          {isSubmitting ? (
            <Spinner
              rotate={true}
              percentage={25}
              hideText
              circumference={24}
              stroke={3}
              className="email-verification__spinner"
              foreground={WHITE}
            />
          ) : (
            I18n.t('transfer.email-verification.submit')
          )}
        </button>
      </div>
    </form>
  );
}

EmailVerification.propTypes = {
  prePopulatedEmail: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
};

export default memo(EmailVerification);
